import { useEffect, useState } from 'react'
import { RefreshCw, X } from 'lucide-react'
import { acceptSwUpdate, SW_UPDATE_EVENT } from '@/lib/swUpdate'

// F3 hardening: a deploy while a tab was open used to be completely silent —
// main.tsx's onNeedRefresh was a no-op, so a long-lived (often installed-PWA)
// tab could run a stale bundle for days with no indication. main.tsx now
// dispatches SW_UPDATE_EVENT when a new service worker is waiting; this banner
// surfaces it. The accept path routes through acceptSwUpdate, which messages
// SKIP_WAITING via the registerSW callback captured in main.tsx (E-10, G1
// audit: the old bare page reload never activated the waiting SW, so the old
// shell re-served and the banner looped forever). The reload then
// happens when the new worker takes control (the plugin's own controlling
// listener), with a bounded plain-reload fallback in acceptSwUpdate. Still
// STRICTLY user-triggered — never automatic, never on tab return (#248/#249
// lock). Do NOT add any auto-reload here.
export function UpdateAvailableBanner() {
  const [updateAvailable, setUpdateAvailable] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const [updating, setUpdating] = useState(false)

  useEffect(() => {
    function handleUpdateAvailable() {
      setUpdateAvailable(true)
      // A fresh signal (e.g. a second deploy after the first was dismissed)
      // re-surfaces the banner — otherwise staleness goes silent again.
      setDismissed(false)
    }
    window.addEventListener(SW_UPDATE_EVENT, handleUpdateAvailable)
    return () => window.removeEventListener(SW_UPDATE_EVENT, handleUpdateAvailable)
  }, [])

  if (!updateAvailable || dismissed) return null

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="flex items-center gap-2 border-b border-primary/20 bg-primary/5 px-4 py-1.5 text-xs text-muted-foreground"
    >
      <RefreshCw className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
      <span>
        A new version is available —{' '}
        <button
          type="button"
          disabled={updating}
          onClick={() => { setUpdating(true); void acceptSwUpdate() }}
          className="rounded font-medium text-primary underline underline-offset-2 hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
        >
          {updating ? 'Updating…' : 'Refresh'}
        </button>
      </span>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss update notice"
        className="ml-auto rounded p-0.5 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  )
}
